import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {Decimal} from 'decimal.js';
import React, {useState} from 'react';

import {
  coreObjectArchiveAction,
  coreObjectUnarchiveAction,
} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {
  createUnit as createUnitHelper,
  CreateUnitParams,
  updateUnit as updateUnitHelper,
  UpdateUnitParams,
  updateUnitType,
} from './UnitDictionaryDAO';
import UnitTableRow from './UnitTableRow';
import UnitTypeInventoryConfig from './UnitTypeInventoryConfig';
import UnitTypeTermsDisplay from './UnitTypeTermsDisplay';
import {isUnitDictionaryManagementEnabledForUser} from './util';

type UnitType = UnitTypesQuery['unitTypes'][number];
type Unit = UnitType['units'][number];

export interface UnitTypeSettingsTableProps {
  unitType: UnitType;
  refetch: () => void;
  queryMatchedUnitsByName: Array<Unit>;
  queryMatchedUnitsBySymbol: Array<Unit>;
}

export default function UnitTypeSettingsTable({
  unitType,
  refetch,
  queryMatchedUnitsByName,
  queryMatchedUnitsBySymbol,
}: UnitTypeSettingsTableProps): JSX.Element {
  const isAdmin = isUnitDictionaryManagementEnabledForUser();
  const [isAddingNewUnit, setIsAddingNewUnit] = useState(false);
  const addUnitCreationRow = (): void => {
    setIsAddingNewUnit(true);
  };

  const addUnitButtonLabel = isAddingNewUnit ? 'Only one unit may be added at a time' : 'Add unit to class';

  const tableHeader = (
    <div className="simpleDivTable-header u-position-relative">
      <div className="simpleDivTable-headerCell simpleDivTable-header--title simpleDivTable-cell--narrow">
        Units
        {isAdmin && (
          <Tooltip container="body" placement={TooltipPlacement.TOP} title={addUnitButtonLabel}>
            <BenchlingIcon
              aria-disabled={isAddingNewUnit}
              aria-label={addUnitButtonLabel}
              className={classNames('simpleDivTable-addButton', {
                'simpleDivTable-addButton--disabled': isAddingNewUnit,
              })}
              {...getActionButtonRoleProps({
                onPress: () => {
                  if (!isAddingNewUnit) {
                    addUnitCreationRow();
                  }
                },
              })}
              icon="compose"
              dataTestAttrs={{component: 'UnitTypeSettingsTable', element: 'addUnitButton'}}
            />
          </Tooltip>
        )}
      </div>
      <div className="simpleDivTable-headerCell simpleDivTable-cell--narrow">Symbol</div>
      <div className="simpleDivTable-headerCell">Conversion factor</div>
      {isAdmin && (
        <div className={classNames('simpleDivTable-actionGroupContainer', css(styles.unitTypeActionButton))}>
          <ArchiveOrUnarchiveButton
            item={unitType}
            onArchive={(itemIds, purpose) => {
              coreObjectArchiveAction(itemIds, purpose);
              refetch();
            }}
            onUnarchive={(itemIds) => {
              coreObjectUnarchiveAction(itemIds);
              refetch();
            }}
            showText={false}
            isArchivable={true}
            isUnarchivable={true}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
          />
        </div>
      )}
    </div>
  );

  const units = unitType.units.slice(); // mutable copy

  units.sort((a: Unit, b: Unit) => {
    if (a.archiveData.archived !== b.archiveData.archived) {
      return a.archiveData.archived ? 1 : -1;
    }

    if (a.id === unitType.baseUnit?.id) {
      return -1;
    } else if (b.id === unitType.baseUnit?.id) {
      return 1;
    } else {
      return new Decimal(a.conversionFactor).comparedTo(new Decimal(b.conversionFactor));
    }
  });

  const createUnit = async (params: CreateUnitParams): Promise<void> => {
    try {
      await createUnitHelper(params);
      notifications.success(`Successfully created unit ${params.symbol}`);
      refetch();
      setIsAddingNewUnit(false);
    } catch (e) {
      if (e.message && e.message.startsWith('Unit symbol')) {
        notifications.warn(e.message);
      } else {
        throw e;
      }
    }
  };

  const updateUnit = async (params: UpdateUnitParams): Promise<void> => {
    try {
      await updateUnitHelper(params);
      notifications.success(`Successfully updated unit ${params.symbol}`);
      refetch();
    } catch (e) {
      if (e.message && e.message.startsWith('Unit symbol')) {
        notifications.warn(e.message);
      } else {
        throw e;
      }
    }
  };

  const tableItems = units.map((unit) => (
    <UnitTableRow
      unit={unit}
      baseUnit={unitType.baseUnit}
      key={unit.id}
      unitTypeId={unitType.id}
      refetch={refetch}
      onSave={updateUnit}
      highlightName={queryMatchedUnitsByName.includes(unit)}
      highlightSymbol={queryMatchedUnitsBySymbol.includes(unit)}
    />
  ));

  if (isAddingNewUnit) {
    tableItems.push(
      <UnitTableRow
        key="addUnit"
        baseUnit={unitType.baseUnit}
        onCancel={() => {
          setIsAddingNewUnit(false);
        }}
        onSave={createUnit}
        unitTypeId={unitType.id}
        refetch={refetch}
      />
    );
  }

  return (
    <div
      className={css(styles.tableContainer)}
      key={unitType.id}
      {...getTestProps({component: 'UnitTypeSettingsTable', element: 'root'})}
    >
      <div className="sampleSchemaSettings-containerTable-name u-flex-display u-vertical-flex">
        <div className="sampleSchemaSettings-containerTable-nameContainer">
          <div className="sampleSchemaSettings-containerTable-nameHeader">Unit type</div>
          {isAdmin && !unitType.archiveData.archived ? (
            <EditableTextField
              requestChange={(newName: string) => {
                if (!newName.trim()) {
                  notifications.error('Cannot set unit type name to be empty');
                  return;
                }
                if (newName.trim() === unitType.name) {
                  return;
                }
                updateUnitType({
                  id: unitType.id,
                  name: newName.trim(),
                });
                notifications.success(`Successfully updated unit type ${newName}`);
                refetch();
              }}
              value={unitType.name}
              dataTestAttrs={{
                component: 'UnitTypeSettingsTable',
                element: 'nameField',
              }}
              emptyValueAllowed={false}
            />
          ) : (
            <div className="u-mini-spacing-top u-mini-spacing-bottom">
              {unitType.name} ({unitType.archiveData.archived && unitType.archiveData.purpose})
            </div>
          )}
        </div>
        {unitType.terms.length > 0 && (
          <div className="sampleSchemaSettings-containerTable-nameContainer">
            <div className="sampleSchemaSettings-containerTable-nameHeader">Formula</div>
            <UnitTypeTermsDisplay terms={unitType.terms} />
          </div>
        )}
        <UnitTypeInventoryConfig unitType={unitType} refetch={refetch} />
      </div>
      <div className={css(styles.divTableWide)}>
        {tableHeader}
        {(() => {
          if (unitType.units.length === 0) {
            return (
              <div className="simpleDivTable-row">
                <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
                  This unit type does not have any units
                </div>
              </div>
            );
          }
          return tableItems;
        })()}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    marginBottom: 26,
    width: '100%',
    border: 1,
    borderStyle: 'solid',
    borderColor: GRAY_LIGHT,
    borderRadius: 4,
    display: 'flex',
  },
  divTableWide: {
    width: '100%',
    minWidth: 500,
    order: 1,
    borderLeft: `1px solid ${GRAY_LIGHT}`,
  },
  unitTypeActionButton: {
    marginRight: '20px',
    marginTop: '-6px',
  },
});
